import { navigate, Router, Location, Match } from "@reach/router";
import React, { Component } from "react";
import { hot } from "react-hot-loader";
import classNames from "classnames";
import About from "~/scripts/components/pages/About";
import Home from "~/scripts/components/pages/Home";
import LineProducers from "~/scripts/components/pages/LineProducers";
import BrowserUpgrade from "~/scripts/components/pages/BrowserUpgrade";
import NotFound from "~/scripts/components/pages/NotFound";
import HeadingPos from "~/scripts/components/HeadingPos"; // Used for finding top / left pos of Heading
import Background from "~/scripts/components/Background";
import GridOverlay from "~/scripts/components/GridOverlay";
import Director from "~/scripts/components/pages/Director";
import Nav from "~/scripts/components/Nav";
import ScrollWrapper from "~/scripts/components/ScrollWrapper";
import VideoPage from "~/scripts/components/VideoPage";
import { AppContext, THEMES } from "~/scripts/context/app";
import TransitionRouter from "~/scripts/components/TransitionRouter";
import Title from "~/scripts/components/Title";
import { isMobile } from "~/scripts/utils/is-mobile";
import Bowser from "bowser";

// Page transition timing
const transitionDuration = 1000;

class App extends Component {
    state = {
        previousPathname: null,
        appContext: {
            theme: THEMES.white,
            browserInfo: {}
        },
        posY: 0,
        scrollLocked: false,
        headingPos: null, // getBoundingClientRect of heading
        specialPageTransition: false,
        transitionDirection: null
    };

    componentDidMount() {
        const info = Bowser.getParser(window.navigator.userAgent).getResult();
        this.setState(({ appContext }) => {
            appContext.browserInfo = info;

            return { appContext };
        });

        if (info.browser.name === "Internet Explorer") {
            navigate("/browser-upgrade");
        }
    }

    setPosY = posY => {
        this.setState({
            posY
        });
    };

    // Saves `heading-pos` rect
    // Used to find out position of heading to the top & left of the screen
    setHeadingPos = headingPos => {
        this.setState({
            headingPos
        });
    };

    setThemeRed = () => {
        this.setState(({ appContext }) => {
            appContext.theme = THEMES.red;

            return { appContext };
        });
    };

    setThemeWhite = () => {
        this.setState(({ appContext }) => {
            appContext.theme = THEMES.white;

            return { appContext };
        });
    };

    setThemeBlack = () => {
        this.setState(({ appContext }) => {
            appContext.theme = THEMES.black;

            return { appContext };
        });
    };

    toggleTheme = setToTheme => {
        this.setState(({ appContext }) => {
            appContext.theme = setToTheme
                ? setToTheme
                : appContext.theme === THEMES.red
                ? THEMES.white
                : THEMES.red;

            return {
                appContext
            };
        });
    };

    // Special page transition toggle
    // Director - Director
    // Home - Director and etc
    // Direction is used for directors / friends transitions
    toggleSpecialPageTransition = (
        value = true,
        direction = null,
        allowMobile = false
    ) => {
        if (isMobile && !allowMobile) return;

        this.setState({
            specialPageTransition: value,
            transitionDirection: direction
        });
    };

    handleEnter = node => {
        if (!isMobile) {
            const { posY } = this.state;
            node.style.transform = `translate3d(0, ${posY}px, 0)`;

            // Lock scroll
            this.setState({
                scrollLocked: true
            });
        }
    };

    handleEntering = node => {
        // If mobile, set timeout to reset the scroll midway the transition
        if (isMobile) {
            setTimeout(() => {
                window.scrollTo(0, 0);
            }, transitionDuration / 2);
        }
    };

    handleEntered = node => {
        this.toggleSpecialPageTransition(false, null, true);

        if (!isMobile) {
            // Reset scroll
            window.scrollTo(0, 0);

            // Unlock the scroll
            // Reset posY - scroll jack position
            this.setState({
                posY: 0,
                scrollLocked: false
            });

            // Remove translate from the entered node
            node.style.transform = ``;
        }
    };

    handleLocationChange = (previousPathname, currentPathname) => {
        this.setState({ previousPathname, currentPathname });
    };

    getFirstSegment = (pathname, defaultSegment = "home") => {
        return pathname && pathname !== "/"
            ? pathname.split("/")[1]
            : defaultSegment;
    };

    render() {
        const {
            posY,
            appContext,
            scrollLocked,
            headingPos,
            previousPathname,
            currentPathname,
            specialPageTransition,
            transitionDirection
        } = this.state;

        const prev = this.getFirstSegment(previousPathname, "home");
        const next = this.getFirstSegment(currentPathname, "home");

        return (
            <Location>
                {({ location }) => (
                    <AppContext.Provider value={appContext}>
                        <Title uri={location.pathname} />
                        <Background />
                        <GridOverlay />
                        <HeadingPos setHeadingPos={this.setHeadingPos} />

                        <Match path="videos/:entryType/:slug/:id">
                            {({ match }) => (match ? null : <Nav />)}
                        </Match>

                        <ScrollWrapper
                            default
                            posY={posY}
                            scrollLocked={scrollLocked}
                            setPosY={this.setPosY}
                        >
                            <TransitionRouter
                                timeout={transitionDuration}
                                location={location}
                                classNames="page"
                                onEnter={this.handleEnter}
                                onEntering={this.handleEntering}
                                onEntered={this.handleEntered}
                                onLocationChange={this.handleLocationChange}
                                className={classNames(
                                    specialPageTransition
                                        ? `transition-${prev}-${next}${
                                              transitionDirection
                                                  ? "-" + transitionDirection
                                                  : ""
                                          }`
                                        : `transition-default`
                                )}
                            >
                                {state => (
                                    <Router location={location} primary={false}>
                                        <Home
                                            path="/"
                                            posY={posY}
                                            state={state}
                                            headingPos={headingPos}
                                            onMount={this.setThemeWhite}
                                            toggleSpecialPageTransition={
                                                this.toggleSpecialPageTransition
                                            }
                                        />

                                        <Director
                                            path="directors/:slug"
                                            onMount={this.toggleTheme}
                                            state={state}
                                            headingPos={headingPos}
                                            transitionDirection={
                                                transitionDirection
                                            }
                                            toggleSpecialPageTransition={
                                                this.toggleSpecialPageTransition
                                            }
                                        />

                                        <LineProducers
                                            path="/line-production"
                                            posY={posY}
                                            state={state}
                                            headingPos={headingPos}
                                            onMount={this.setThemeBlack}
                                            transitionDirection={
                                                transitionDirection
                                            }
                                            toggleSpecialPageTransition={
                                                this.toggleSpecialPageTransition
                                            }
                                        />

                                        {/* 
                                        <LineProducer
                                            path="line-production/:slug"
                                            onMount={this.toggleTheme}
                                            state={state}
                                            headingPos={headingPos}
                                            transitionDirection={
                                                transitionDirection
                                            }
                                            toggleSpecialPageTransition={
                                                this.toggleSpecialPageTransition
                                            }
                                        /> */}

                                        {/* <Director
                                            friend={true}
                                            path="friends/:slug"
                                            onMount={this.setThemeBlack}
                                            state={state}
                                            headingPos={headingPos}
                                            transitionDirection={
                                                transitionDirection
                                            }
                                            toggleSpecialPageTransition={
                                                this.toggleSpecialPageTransition
                                            }
                                        /> */}

                                        <About
                                            path="about"
                                            onMount={this.setThemeRed}
                                        />
                                        <BrowserUpgrade
                                            path="browser-upgrade"
                                            onMount={this.setThemeWhite}
                                        />
                                        <NotFound
                                            default
                                            onMount={this.setThemeWhite}
                                        />

                                        <VideoPage
                                            path="videos/:entryType/:slug/:id"
                                            onMount={this.setThemeBlack}
                                            toggleSpecialPageTransition={
                                                this.toggleSpecialPageTransition
                                            }
                                        />
                                    </Router>
                                )}
                            </TransitionRouter>
                        </ScrollWrapper>
                    </AppContext.Provider>
                )}
            </Location>
        );
    }
}

/* eslint-disable-next-line no-undef */
export default hot(module)(App);



// WEBPACK FOOTER //
// ./src/scripts/components/App.js